"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  closeDrawer,
  removeLine,
  updateQty,
  useCart,
  type CartLine,
} from "@/lib/cart-store";
import { lineToItem, trackRemoveFromCart } from "@/lib/analytics";
import { formatPrice } from "@/app/data/products";
import { useT } from "./LocaleProvider";

/**
 * Slide-in cart drawer.
 *
 * Built on native <dialog> so we inherit the focus trap, Escape-to-close,
 * and ::backdrop for free. We override the default centering with
 * right-anchored fixed positioning to slide in from the edge.
 *
 * Singleton — mounted once in app/layout.tsx. Any consumer fires
 * openDrawer() from lib/cart-store and the dialog reacts via useCart().
 *
 * Phase 1: Checkout CTA navigates to /cart. Phase 2 will swap to
 * Wix Redirects (createRedirectSession) and bounce to hosted checkout.
 */
export function CartDrawer() {
  const { lines, subtotalCents, drawerOpen } = useCart();
  const ref = useRef<HTMLDialogElement>(null);
  const t = useT();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (drawerOpen && !d.open) {
      d.showModal();
    } else if (!drawerOpen && d.open) {
      d.close();
    }
  }, [drawerOpen]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) closeDrawer();
  }

  return (
    <dialog
      ref={ref}
      onClose={closeDrawer}
      onClick={handleBackdropClick}
      aria-labelledby="cart-drawer-title"
      className="cart-drawer fixed right-0 top-0 m-0 h-full max-h-full w-full max-w-sm border-l border-[var(--color-bone)]/15 bg-[var(--color-ink)] p-0 text-[var(--color-bone)] shadow-2xl backdrop:bg-black/70 backdrop:backdrop-blur-sm"
    >
      <div className="flex h-full flex-col">
        <header className="flex items-center justify-between border-b border-[var(--color-bone)]/10 px-5 py-4">
          <div>
            <div className="mono-strip text-[10px] text-[var(--color-foil)]">
              {t({ en: "Your bag", es: "Tu bolsa" })}
            </div>
            <h2
              id="cart-drawer-title"
              className="font-display text-lg uppercase leading-tight"
            >
              {lines.length === 0
                ? t({ en: "Empty", es: "Vacía" })
                : `${lines.length} ${
                    lines.length === 1
                      ? t({ en: "item", es: "artículo" })
                      : t({ en: "items", es: "artículos" })
                  }`}
            </h2>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            aria-label="Close bag"
            className="-mr-1 inline-flex h-9 w-9 items-center justify-center rounded-full text-[var(--color-bone-dim)] transition hover:bg-[var(--color-bone)]/10 hover:text-[var(--color-bone)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </header>

        {lines.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="flex-1 divide-y divide-[var(--color-bone)]/10 overflow-y-auto [overscroll-behavior:contain] [-webkit-overflow-scrolling:touch]">
            {lines.map((line) => (
              <DrawerLine key={line.id} line={line} />
            ))}
          </ul>
        )}

        {lines.length > 0 && (
          <footer className="border-t border-[var(--color-bone)]/10 bg-[var(--color-ink)] px-5 py-5">
            <div className="mb-3 flex items-baseline justify-between">
              <span className="mono-strip text-[11px] text-[var(--color-bone-dim)]">
                {t({ en: "Subtotal", es: "Subtotal" })}
              </span>
              <span className="font-mono text-xl">
                {formatPrice(subtotalCents)}
              </span>
            </div>
            <p className="mb-4 font-mono text-[11px] leading-relaxed text-[var(--color-bone-dim)]">
              {t({
                en: "Flat $9.99 shipping.",
                es: "Envío plano $9.99.",
              })}
            </p>
            <Link
              href="/cart"
              onClick={closeDrawer}
              className="mono-strip block w-full rounded-sm bg-[var(--color-blood)] px-6 py-3 text-center text-[11px] text-white transition hover:bg-[var(--color-blood)]/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
            >
              {t({ en: "Review & checkout →", es: "Revisar y pagar →" })}
            </Link>
            <button
              type="button"
              onClick={closeDrawer}
              className="mono-strip mt-2.5 block w-full rounded-sm border border-[var(--color-bone)]/30 px-6 py-2.5 text-center text-[11px] text-[var(--color-bone)] transition hover:border-[var(--color-foil)] hover:text-[var(--color-foil)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
            >
              {t({ en: "Continue shopping", es: "Seguir comprando" })}
            </button>
          </footer>
        )}
      </div>

      <style jsx global>{`
        .cart-drawer {
          transform: translateX(0);
          opacity: 1;
          transition: transform 220ms ease-out, opacity 220ms ease-out;
        }
        @starting-style {
          .cart-drawer[open] {
            transform: translateX(100%);
            opacity: 0;
          }
        }
        .cart-drawer::backdrop {
          opacity: 1;
          transition: opacity 220ms ease-out;
        }
        @starting-style {
          .cart-drawer[open]::backdrop {
            opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .cart-drawer,
          .cart-drawer::backdrop {
            transition: none !important;
          }
          @starting-style {
            .cart-drawer[open] {
              transform: translateX(0);
              opacity: 1;
            }
            .cart-drawer[open]::backdrop {
              opacity: 1;
            }
          }
        }
      `}</style>
    </dialog>
  );
}

function EmptyState() {
  const t = useT();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[var(--color-bone)]/15">
        <BagIcon className="h-6 w-6 text-[var(--color-bone-dim)]" />
      </div>
      <p className="font-display text-lg uppercase">
        {t({ en: "Your bag is empty", es: "Tu bolsa está vacía" })}
      </p>
      <p className="max-w-[14rem] font-mono text-[11px] leading-relaxed text-[var(--color-bone-dim)]">
        {t({
          en: "Nothing here yet. Pull up the catalog and grab a pair.",
          es: "Nada aquí todavía. Mira el catálogo y agrega un par.",
        })}
      </p>
      <Link
        href="/shop"
        onClick={closeDrawer}
        className="mono-strip mt-2 rounded-sm border border-[var(--color-bone)]/30 px-5 py-2.5 text-[10px] text-[var(--color-bone)] transition hover:border-[var(--color-blood)] hover:text-[var(--color-blood)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
      >
        {t({ en: "Browse the shop →", es: "Ver la tienda →" })}
      </Link>
    </div>
  );
}

function DrawerLine({ line }: { line: CartLine }) {
  const lineTotal = line.priceCents * line.qty;
  return (
    <li className="flex gap-3 px-5 py-4">
      <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-sm bg-white">
        {line.imageSrc ? (
          <Image
            src={line.imageSrc}
            alt=""
            fill
            sizes="80px"
            className="object-contain p-1"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-mono text-[10px] text-[var(--color-ink)]/40">
            {line.sku}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <Link
              href={`/shop/${line.productId}`}
              onClick={closeDrawer}
              className="block font-display text-sm uppercase leading-tight hover:text-[var(--color-foil)]"
            >
              {line.name}
            </Link>
            <div className="mono-strip mt-0.5 text-[10px] text-[var(--color-bone-dim)]">
              Size US {line.size} · {line.sku}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              trackRemoveFromCart(lineToItem(line));
              removeLine(line.id);
            }}
            aria-label={`Remove ${line.name} size ${line.size}`}
            className="-mr-1 -mt-1 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[var(--color-bone-dim)] transition hover:bg-[var(--color-bone)]/10 hover:text-[var(--color-blood)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
          >
            <CloseIcon className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="mt-2.5 flex items-center justify-between">
          <QtyStepper line={line} />
          <span className="font-mono text-sm">{formatPrice(lineTotal)}</span>
        </div>
      </div>
    </li>
  );
}

export function QtyStepper({ line }: { line: CartLine }) {
  return (
    <div className="inline-flex items-center rounded-sm border border-[var(--color-bone)]/20">
      <button
        type="button"
        onClick={() => updateQty(line.id, line.qty - 1)}
        aria-label={`Decrease quantity of ${line.name} size ${line.size}`}
        className="inline-flex h-7 w-7 items-center justify-center text-[var(--color-bone-dim)] transition hover:text-[var(--color-bone)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
      >
        <MinusIcon className="h-3 w-3" />
      </button>
      <span
        aria-live="polite"
        aria-label={`Quantity ${line.qty}`}
        className="min-w-[1.5rem] text-center font-mono text-xs"
      >
        {line.qty}
      </span>
      <button
        type="button"
        onClick={() => updateQty(line.id, line.qty + 1)}
        aria-label={`Increase quantity of ${line.name} size ${line.size}`}
        className="inline-flex h-7 w-7 items-center justify-center text-[var(--color-bone-dim)] transition hover:text-[var(--color-bone)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
      >
        <PlusIcon className="h-3 w-3" />
      </button>
    </div>
  );
}

// ─── Icons ─────────────────────────────────────────────────────────
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M12 5v14M5 12h14"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MinusIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M5 12h14"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BagIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M5 8h14l-1 11.5a2 2 0 0 1-2 1.8H8a2 2 0 0 1-2-1.8L5 8z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M9 8V6a3 3 0 1 1 6 0v2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
